import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Book as BookIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  ContactMail as ContactMailIcon,
  Description as DescriptionIcon,
  ExitToApp as ExitToAppIcon,
  Group as GroupIcon,
  HomeRounded as HomeRoundedIcon,
  MenuRounded as MenuRoundedIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import logo from '../assets/logo.png';

const DRAWER_WIDTH = 292;

const NAVIGATION = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/', icon: <HomeRoundedIcon />, roles: ['Admin', 'Editor', 'User'] },
    ],
  },
  {
    label: 'Content',
    items: [
      { label: 'Blogs', path: '/blogs', icon: <BookIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Authors', path: '/authors', icon: <PersonIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Resources', path: '/resources', icon: <DescriptionIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'White Papers', path: '/whitepapers', icon: <ArticleIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Page Content', path: '/page-contents', icon: <ArticleIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Global Settings', path: '/global-settings', icon: <SettingsIcon />, roles: ['Admin', 'Editor', 'User'] },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Services', path: '/services', icon: <BuildIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Clients', path: '/clients', icon: <BusinessIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Team', path: '/team', icon: <GroupIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Careers', path: '/careers', icon: <WorkIcon />, roles: ['Admin', 'Editor', 'User'] },
      { label: 'Applications', path: '/applications', icon: <AssignmentIcon />, roles: ['Admin', 'Editor'] },
      { label: 'Contacts', path: '/contacts', icon: <ContactMailIcon />, roles: ['Admin', 'Editor'] },
      { label: 'White Paper Requests', path: '/whitepaper-requests', icon: <DescriptionIcon />, roles: ['Admin', 'Editor'] },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Users', path: '/users', icon: <PeopleIcon />, roles: ['Admin'] },
    ],
  },
];

function isItemVisible(item, role) {
  if (!item.roles || item.roles.length === 0) {
    return true;
  }

  return item.roles.includes(role || 'User');
}

function isPathActive(itemPath, pathname) {
  if (itemPath === '/') {
    return pathname === '/';
  }

  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function DashboardLayout({ window, children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);

  const visibleSections = useMemo(
    () =>
      NAVIGATION.map((section) => ({
        ...section,
        items: section.items.filter((item) => isItemVisible(item, user?.role)),
      })).filter((section) => section.items.length > 0),
    [user?.role]
  );

  const activeItem = useMemo(() => {
    for (const section of visibleSections) {
      const match = section.items.find((item) => isPathActive(item.path, location.pathname));
      if (match) {
        return match;
      }
    }

    return null;
  }, [location.pathname, visibleSections]);

  const container = window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', px: 2, py: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          borderColor: 'rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.03)',
          color: 'inherit',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={logo}
            alt="MeGuide Consulting"
            sx={{ width: 44, height: 44, borderRadius: 2, objectFit: 'contain', bgcolor: '#fff', p: 0.5 }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#fff' }}>
              MeGuide Admin
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(234,241,255,0.72)' }}>
              Content and operations hub
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ mt: 3, flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {visibleSections.map((section) => (
          <List
            key={section.label}
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'rgba(226,232,240,0.48)',
                  fontSize: '0.76rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  lineHeight: 1.4,
                  mb: 1,
                }}
              >
                {section.label}
              </ListSubheader>
            }
            sx={{ mb: 2 }}
          >
            {section.items.map((item) => {
              const selected = isPathActive(item.path, location.pathname);

              return (
                <ListItemButton
                  key={item.path}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    if (!isDesktop) {
                      setMobileOpen(false);
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: selected ? '#fff' : 'rgba(234,241,255,0.82)', minWidth: 38 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: selected ? 700 : 600,
                      color: selected ? '#fff' : 'rgba(226,232,240,0.88)',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 1,
          p: 2,
          borderRadius: 3,
          borderColor: 'rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.03)',
          color: 'inherit',
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(234,241,255,0.72)' }}>
          Signed in as
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 700, color: '#fff' }}>
          {user?.username || user?.email || 'Administrator'}
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="default"
        sx={{
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { lg: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ minHeight: '82px !important', px: { xs: 2, md: 4 } }}>
          {!isDesktop && (
            <IconButton
              color="inherit"
              onClick={() => setMobileOpen(true)}
              edge="start"
              sx={{ mr: 1.5 }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              MeGuide Consulting
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {activeItem?.label || 'Dashboard'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center">
            <Chip
              label={user?.role || 'User'}
              color="secondary"
              variant="outlined"
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: 'background.paper' }}
            />
            <Avatar sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 800 }}>
              {String(user?.username || user?.email || 'A').charAt(0).toUpperCase()}
            </Avatar>
            <Button
              color="inherit"
              onClick={logout}
              startIcon={<ExitToAppIcon />}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Logout
            </Button>
            <IconButton color="inherit" onClick={logout} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
              <ExitToAppIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          ml: { lg: `${DRAWER_WIDTH}px` },
          pt: { xs: '98px', md: '108px' },
          px: { xs: 2, md: 3 },
          pb: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
}

DashboardLayout.propTypes = {
  window: PropTypes.func,
  children: PropTypes.node,
};

export default DashboardLayout;
